/**
 * Utility functions for partner request operations.
 *
 * Provides helper functions for sorting and ordering partner requests,
 * including level-based sorting and status prioritization.
 *
 * @module partnerRequestUtils
 */

import { PartnerRequest, GermanLevel } from '@/types/partner-finder';

/**
 * Order of German levels for sorting purposes
 */
const LEVEL_ORDER: GermanLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

/**
 * Gets the sort order index for a German proficiency level.
 *
 * Returns the position of a level in the standard CEFR progression (A1 → C2)
 * for use in sorting operations. Lower levels (A1) have lower indices.
 *
 * @param level - German proficiency level (A1, A2, B1, B2, C1, or C2)
 * @returns Index in LEVEL_ORDER array (0-5), or -1 if level is not found
 *
 * @example
 * ```typescript
 * getLevelOrder('A1'); // Returns 0
 * getLevelOrder('B2'); // Returns 3
 * ```
 */
export function getLevelOrder(level: GermanLevel): number {
  return LEVEL_ORDER.indexOf(level);
}

/**
 * Sorts partner requests with status and level prioritization.
 *
 * Comparison function for Array.sort() that prioritizes active requests over
 * paused/expired requests, and within each status group, sorts by proficiency
 * level (A1 → C2).
 *
 * @param a - First partner request to compare
 * @param b - Second partner request to compare
 * @returns Comparison result:
 *   - Negative number if a should come before b
 *   - Positive number if a should come after b
 *   - Zero if a and b are equal in sort order
 *
 * @example
 * ```typescript
 * const sorted = requests.sort(sortRequests);
 * // Active requests appear first, sorted by level (A1, A2, B1, ...)
 * // Then paused/expired requests, also sorted by level
 * ```
 */
export function sortRequests(a: PartnerRequest, b: PartnerRequest): number {
  // Active requests first
  if (a.status === 'active' && b.status !== 'active') return -1;
  if (a.status !== 'active' && b.status === 'active') return 1;

  // Within same status, sort by level
  const levelOrderA = getLevelOrder(a.level);
  const levelOrderB = getLevelOrder(b.level);
  return levelOrderA - levelOrderB;
}

